/* ============================================================
   Whisk Hospitality — Collection redesign behaviour
   - Fits-my-space: convenience control that ticks native size-band facets
   - Add to Quote: wires cards into window.completeQuoteCart
   - Recently viewed: localStorage scroller (seeded on card click)
   Cooperates with assets/facet-filters.js (re-runs after on:facet-filters:updated).
   ============================================================ */
(function () {
  'use strict';

  // Matched against facet input names. The store exposes the dimension metafields as
  // plain `list` facets (filter.p.m.custom.width_mm etc.) whose values are raw integers
  // — ~100 checkboxes per dimension, which is exactly what "Fits my space" replaces.
  // Band-style values (e.g. "500–700 mm") are also understood, so this keeps working if
  // the bucket data job later replaces the raw values.
  var DIMENSIONS = [
    { key: 'custom.width_mm', label: 'Width', ph: '900' },
    { key: 'custom.depth_mm', label: 'Depth', ph: '800' },
    { key: 'custom.height_mm', label: 'Height', ph: '1100' }
  ];

  // What the shopper typed, kept outside the DOM. facet-filters.js replaces the whole
  // #facets form on every AJAX update, destroying the injected Fits-my-space block, so a
  // partially typed value ("9" on the way to "900") would otherwise be lost: nothing
  // matches 9mm, so no checkbox stays ticked and there is nothing to restore from.
  var fitsValues = {};
  var lastFitsKey = null;

  /* ---------- helpers ---------- */
  function root() { return document.querySelector('.wc-collection'); }

  // Does a facet value fit within `max` mm? Handles the raw integers the store currently
  // exposes ("900") as well as band strings ("≤500 mm", "500–700 mm", ">1200 mm").
  function valueFits(value, max) {
    if (!value) return false;
    var t = String(value).trim();
    var nums = (t.match(/\d+/g) || []).map(Number);
    if (!nums.length) return false;
    if (/^[>≥]/.test(t)) return false;              // open-ended upper band never guaranteed
    if (/^[<≤]/.test(t)) return nums[0] <= max;     // "≤500"
    if (nums.length >= 2) return nums[1] <= max;    // "500–700" -> needs upper bound to fit
    return nums[0] <= max;                          // raw "900"
  }

  // Largest mm value represented by a facet value, for reflecting state back into inputs.
  function valueUpper(value) {
    var nums = (String(value).match(/\d+/g) || []).map(Number);
    if (!nums.length) return 0;
    return nums[nums.length - 1];
  }

  // Find checkbox inputs in the facet form for a given dimension bucket key.
  function bandInputsFor(key) {
    var form = document.getElementById('facets');
    if (!form) return [];
    return Array.prototype.filter.call(
      form.querySelectorAll('input[type="checkbox"][name]'),
      function (input) { return input.name.indexOf(key) !== -1; }
    );
  }

  function dimensionAvailable(key) { return bandInputsFor(key).length > 0; }

  /* ---------- Fits my space ---------- */
  function buildFits() {
    var form = document.getElementById('facets');
    if (!form) return;
    if (form.querySelector('[data-wc-fits]')) return; // already injected
    var section = root();
    if (!section) return;
    if (section.getAttribute('data-wc-fits-enabled') === 'false') return;

    // Only render the helper once the size-band facets actually exist (i.e. the
    // dimension data job has run). A control that can't filter anything is worse
    // than no control, so we render nothing until then.
    var available = DIMENSIONS.filter(function (d) { return dimensionAvailable(d.key); });
    if (!available.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'wc-fits';
    wrap.setAttribute('data-wc-fits', '');

    var fields = available.map(function (d) {
      return (
        '<div class="wc-fits__field">' +
        '<label for="wc-fits-' + d.key + '">' + d.label + '</label>' +
        '<input type="number" min="0" inputmode="numeric" id="wc-fits-' + d.key + '" ' +
        'data-wc-fits-input="' + d.key + '" placeholder="' + d.ph + '" aria-label="Maximum ' + d.label.toLowerCase() + ' in millimetres">' +
        '</div>'
      );
    }).join('');

    wrap.innerHTML =
      '<div class="wc-fits__title">Fits my space</div>' +
      '<p class="wc-fits__hint">Enter your bench cut-out (mm). We\'ll show products that fit.</p>' +
      '<div class="wc-fits__grid" style="grid-template-columns:repeat(' + available.length + ',1fr)">' + fields + '</div>' +
      '<button type="button" class="wc-fits__clear" data-wc-fits-clear hidden>Clear footprint</button>';

    // Insert at the very top of the filter list so it sits inside the drawer on mobile.
    var filters = form.querySelector('.facets__filters') || form;
    filters.insertBefore(wrap, filters.firstChild);

    // Re-seed from what the shopper typed before this rebuild.
    Object.keys(fitsValues).forEach(function (k) {
      var el = wrap.querySelector('[data-wc-fits-input="' + k + '"]');
      if (el && fitsValues[k] !== '') el.value = fitsValues[k];
    });

    restoreFitsFromUrl();
  }

  // Reflect any active band selections back into the numeric inputs (approx: show the
  // largest selected band upper bound), and toggle the clear button.
  function restoreFitsFromUrl() {
    var anyActive = false;
    DIMENSIONS.forEach(function (d) {
      var input = document.querySelector('[data-wc-fits-input="' + d.key + '"]');
      if (!input) return;
      var checked = bandInputsFor(d.key).filter(function (i) { return i.checked; });
      if (checked.length) {
        anyActive = true;
        var maxHi = 0;
        checked.forEach(function (i) {
          var hi = valueUpper(i.value);
          if (hi > maxHi) maxHi = hi;
        });
        if (maxHi && !input.value) input.value = maxHi;
      }
    });
    var clearBtn = document.querySelector('[data-wc-fits-clear]');
    if (clearBtn) clearBtn.hidden = !anyActive;
  }

  // Tick every band that could contain a fitting item (band lower bound < entered max),
  // then let facet-filters.js apply the change.
  // Ticking nothing means "no constraint" to Shopify, not "nothing fits". When a shopper
  // enters a size smaller than every value the facet exposes, that silently dropped the
  // constraint and let oversized products through. Submit a value no product can have so
  // the query honestly returns nothing instead.
  function setNoMatchSentinel(key, on) {
    var form = document.getElementById('facets');
    if (!form) return;
    var id = 'wc-fits-nomatch-' + key;
    var el = document.getElementById(id);
    if (on) {
      if (!el) {
        el = document.createElement('input');
        el.type = 'checkbox';
        el.id = id;
        el.hidden = true;
        el.name = 'filter.p.m.' + key;
        el.value = '0';   // dimensions are positive integers, so 0 matches nothing
        form.appendChild(el);
      }
      el.checked = true;
    } else if (el) {
      el.remove();
    }
  }

  function applyFits(key, value) {
    var inputs = bandInputsFor(key);
    if (!inputs.length) return;
    var max = parseInt(value, 10);
    var hasMax = !isNaN(max) && max > 0;
    var matched = 0;

    inputs.forEach(function (input) {
      var shouldCheck = hasMax && valueFits(input.value, max);
      if (shouldCheck) matched++;
      if (input.checked !== shouldCheck) {
        input.checked = shouldCheck;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    setNoMatchSentinel(key, hasMax && matched === 0);
    renderFitsWarning();
  }

  // Tell the shopper which dimension has nothing available, rather than just showing an
  // empty grid.
  function renderFitsWarning() {
    var wrap = document.querySelector('[data-wc-fits]');
    if (!wrap) return;
    var el = wrap.querySelector('.wc-fits__warn');
    var impossible = DIMENSIONS.filter(function (d) {
      var v = parseInt(fitsValues[d.key], 10);
      if (isNaN(v) || v <= 0) return false;
      return !bandInputsFor(d.key).some(function (i) { return valueFits(i.value, v); });
    });
    if (!impossible.length) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement('p');
      el.className = 'wc-fits__warn';
      wrap.appendChild(el);
    }
    el.textContent = 'No products are small enough for that ' +
      impossible.map(function (d) { return d.label.toLowerCase(); }).join(' or ') + '.';
  }

  // Re-apply every cached footprint value (used after facet-filters.js rebuilds the form).
  function syncAllFits() {
    DIMENSIONS.forEach(function (d) {
      var v = fitsValues[d.key];
      if (v !== undefined && v !== '') applyFits(d.key, v);
    });
  }

  function clearFits() {
    fitsValues = {};
    lastFitsKey = null;
    DIMENSIONS.forEach(function (d) { setNoMatchSentinel(d.key, false); });
    var warn = document.querySelector('.wc-fits__warn');
    if (warn) warn.remove();
    DIMENSIONS.forEach(function (d) {
      var input = document.querySelector('[data-wc-fits-input="' + d.key + '"]');
      if (input) input.value = '';
      bandInputsFor(d.key).forEach(function (i) {
        if (i.checked) {
          i.checked = false;
          i.dispatchEvent(new Event('input', { bubbles: true }));
          i.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* ---------- Add to Quote ---------- */
  function addToQuote(btn) {
    var data = {
      product_id: btn.getAttribute('data-product-id'),
      variant_id: btn.getAttribute('data-variant-id'),
      quantity: 1,
      image: btn.getAttribute('data-image') || '',
      title: btn.getAttribute('data-title') || '',
      sku: btn.getAttribute('data-sku') || '',
      price: parseInt(btn.getAttribute('data-price'), 10) || 0,
      formatted_price: btn.getAttribute('data-formatted-price') || '',
      dimensions: btn.getAttribute('data-dimensions') || ''
    };
    // Await readiness instead of sampling the global. The fallback stays for the case
    // where the runtime genuinely failed to load, not merely "hasn't loaded yet".
    var ready = (window.whiskQuote && window.whiskQuote.ready) || Promise.resolve(window.completeQuoteCart);
    ready.then(function (cart) {
      if (cart && typeof cart.addProduct === 'function') {
        cart.addProduct(data);
      } else {
        window.location.href = '/pages/quote';
      }
    });
  }

  /* ---------- Recently viewed ---------- */
  var RV_KEY = 'wc_recently_viewed';
  var RV_MAX = 12;

  function rvRead() {
    try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; }
  }
  function rvWrite(list) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX))); } catch (e) {}
  }
  function rvRecordFromCard(card) {
    if (!card) return;
    var link = card.querySelector('.wc-card__title a, .wc-card__media');
    var quote = card.querySelector('[data-wc-quote]');
    if (!link || !quote) return;
    var item = {
      id: card.getAttribute('data-id'),
      url: link.getAttribute('href'),
      title: quote.getAttribute('data-title') || '',
      brand: (card.querySelector('.wc-card__brand') || {}).textContent || '',
      price: quote.getAttribute('data-formatted-price') || '',
      image: quote.getAttribute('data-image') || ''
    };
    if (!item.id || !item.url) return;
    var list = rvRead().filter(function (x) { return x.id !== item.id; });
    list.unshift(item);
    rvWrite(list);
  }
  function rvRender() {
    var section = document.querySelector('[data-wc-recent]');
    if (!section) return;
    var list = rvRead();
    var wrap = section.querySelector('[data-wc-recent-list]');
    if (!list.length) { section.hidden = true; return; }
    wrap.innerHTML = list.map(function (p) {
      return (
        '<a class="wc-recent__card" href="' + p.url + '">' +
        '<div class="wc-recent__img">' + (p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '') + '</div>' +
        (p.brand ? '<div class="wc-recent__brand">' + p.brand + '</div>' : '') +
        '<div class="wc-recent__name">' + p.title + '</div>' +
        (p.price ? '<div class="wc-recent__price">' + p.price + '</div>' : '') +
        '</a>'
      );
    }).join('');
    section.hidden = false;
  }

  /* ---------- wiring ---------- */
  function onClick(e) {
    var quoteBtn = e.target.closest('[data-wc-quote]');
    if (quoteBtn) { e.preventDefault(); addToQuote(quoteBtn); return; }

    if (e.target.closest('[data-wc-fits-clear]') || e.target.closest('[data-wc-fits-chip]')) { clearFits(); return; }

    var descBtn = e.target.closest('[data-wc-desc-toggle]');
    if (descBtn) { toggleDesc(descBtn); return; }

    if (e.target.closest('[data-wc-recent-clear]')) {
      rvWrite([]); rvRender(); return;
    }

    var cardLink = e.target.closest('.wc-card__title a, .wc-card__media');
    if (cardLink) { rvRecordFromCard(cardLink.closest('.wc-card')); }
  }

  var fitsDebounce;
  function onInput(e) {
    if (e.target.id && e.target.id.indexOf('price-range') === 0) { updatePriceTrack(); }

    var fitsInput = e.target.closest('[data-wc-fits-input]');
    if (!fitsInput) return;
    clearTimeout(fitsDebounce);
    var key = fitsInput.getAttribute('data-wc-fits-input');
    var value = fitsInput.value;
    fitsValues[key] = value;
    lastFitsKey = key;
    fitsDebounce = setTimeout(function () {
      applyFits(key, value);
      var clearBtn = document.querySelector('[data-wc-fits-clear]');
      if (clearBtn) clearBtn.hidden = false;
    }, 700);
  }

  /* ---------- Filter rail header ----------
     The shared facet-filters snippet has no desktop "Filters" heading, and its counts
     render as "(148)". Both come from a snippet the search page also uses, so adjust
     them here rather than editing shared markup. */
  function buildRailHead() {
    var form = document.getElementById('facets');
    if (!form || form.querySelector('.wc-railhead')) return;

    var head = document.createElement('div');
    head.className = 'wc-railhead';

    var title = document.createElement('h2');
    title.className = 'wc-railhead__title';
    title.textContent = 'Filters';

    var actions = document.createElement('div');
    actions.className = 'wc-railhead__actions';

    // Reuse the theme's own clear-all URL so it clears filters while preserving sort.
    var themeClear = form.querySelector('.js-clear-all');
    if (themeClear) {
      var clear = document.createElement('a');
      clear.className = 'wc-railhead__clear';
      clear.href = themeClear.getAttribute('href');
      clear.textContent = 'Clear all';
      actions.appendChild(clear);
    }

    var collapse = document.createElement('button');
    collapse.type = 'button';
    collapse.className = 'wc-railhead__collapse';
    collapse.setAttribute('aria-label', 'Collapse filters');
    collapse.title = 'Collapse filters';
    collapse.textContent = '«';
    collapse.addEventListener('click', function () {
      var toggle = document.querySelector('.js-toggle-filters');
      if (toggle) toggle.click();
    });
    actions.appendChild(collapse);

    head.appendChild(title);
    head.appendChild(actions);
    form.insertBefore(head, form.firstChild);
  }

  /* ---------- Price filter ----------
     The design labels the fields "Min"/"Max" and states the bounds under the track,
     rather than showing a currency mark inside each box. Both come from the shared
     price-range snippet, so adjust them here. */
  function decoratePriceFilter() {
    var min = document.getElementById('price-range-number-min');
    var max = document.getElementById('price-range-number-max');
    if (!min || !max) return;
    min.placeholder = 'Min';
    max.placeholder = 'Max';

    var sliders = document.querySelector('.price-range__sliders');
    if (!sliders || !sliders.parentElement) return;
    if (sliders.parentElement.querySelector('.wc-price-bounds')) return;

    var lo = Number(min.getAttribute('min') || 0);
    var hi = Number(max.getAttribute('max') || 0);
    if (!hi) return;

    // The snippet renders the shop's currency symbol next to each input; reuse it rather
    // than hardcoding one, and lead with it ("$79,310", not "79,310$").
    var symbolEl = document.querySelector('.price-range__currency');
    var symbol = symbolEl ? symbolEl.textContent.trim() : '$';

    var bounds = document.createElement('div');
    bounds.className = 'wc-price-bounds';
    [lo, hi].forEach(function (v) {
      var span = document.createElement('span');
      span.textContent = symbol + v.toLocaleString();
      bounds.appendChild(span);
    });
    sliders.insertAdjacentElement('afterend', bounds);

    updatePriceTrack();
  }

  // The design's track is two-tone: dark between the handles, light outside. The theme's
  // price-range.js publishes no handle positions, so compute them and hand the stops to
  // CSS as custom properties.
  function updatePriceTrack() {
    var sliders = document.querySelector('.price-range__sliders');
    var lo = document.getElementById('price-range-slider-min');
    var hi = document.getElementById('price-range-slider-max');
    if (!sliders || !lo || !hi) return;
    var min = Number(lo.min || 0);
    var max = Number(hi.max || 0);
    if (!(max > min)) return;
    function pct(v) { return Math.max(0, Math.min(100, ((Number(v) - min) / (max - min)) * 100)); }
    sliders.style.setProperty('--wc-range-lo', pct(lo.value) + '%');
    sliders.style.setProperty('--wc-range-hi', pct(hi.value) + '%');
  }

  // Only Price starts open; the rest stay collapsed so the rail is scannable. Rendered
  // collapsed server-side, so this just opens the exceptions.
  function openDefaultGroups() {
    var price = document.querySelector('#facets details.filter[data-type="price_range"]');
    if (price) price.open = true;
    // If filters are applied, show them rather than hiding them behind a closed summary.
    var applied = document.getElementById('active-filters');
    if (applied && document.querySelector('.active-filters .active-filter')) applied.open = true;
  }

  // Theme renders counts as "(148)"; the design shows a bare number.
  function stripCountParens() {
    Array.prototype.forEach.call(document.querySelectorAll('#facets .filter__count'), function (el) {
      var t = el.textContent.trim();
      var m = t.match(/^\((.*)\)$/);
      if (m) el.textContent = m[1];
    });
  }

  /* ---------- Active-filter chips for dimensions ----------
     Fits-my-space ticks every dimension value at or below the entered size, so the theme
     renders one chip per value (35+ of them). Hide those and show a single summary chip
     instead, matching the design. */
  function syncDimensionChips() {
    var wrap = document.querySelector('.active-filters');
    if (!wrap) return;

    var active = DIMENSIONS.map(function (d) {
      var vals = bandInputsFor(d.key).filter(function (i) { return i.checked; })
        .map(function (i) { return valueUpper(i.value); });
      return { label: d.label, max: vals.length ? Math.max.apply(null, vals) : null, values: vals };
    }).filter(function (x) { return x.max !== null; });

    var dimValues = {};
    active.forEach(function (x) { x.values.forEach(function (v) { dimValues[v] = true; }); });

    Array.prototype.forEach.call(wrap.querySelectorAll('.active-filter'), function (chip) {
      var txt = chip.textContent.trim();
      chip.hidden = /^\d+$/.test(txt) && dimValues[Number(txt)] === true;
    });

    var existing = wrap.querySelector('[data-wc-fits-chip]');
    if (existing) existing.remove();
    if (!active.length) return;

    var summary = active.map(function (x) { return x.label.charAt(0) + '≤' + x.max; }).join(' · ');
    var chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'active-filter flex items-center text-current';
    chip.setAttribute('data-wc-fits-chip', '');
    chip.setAttribute('aria-label', 'Clear footprint filter');

    /* .active-filter is `flex-direction: row-reverse`, so the theme's chips put the icon
       first in the DOM and it renders on the right. A single text node has nothing to
       reorder, which is why "✕ Fits …" kept its ✕ on the left while every native chip had
       it on the right. Mirror the theme's structure: icon first in source, label second.

       The icon is the theme's own close SVG rather than a "✕" character — a text glyph
       rendered ~9px wide against the native chips' 24px icon, which is what made this pill
       look like a different component. Same markup means same size, weight and stroke. */
    var NS = 'http://www.w3.org/2000/svg';
    var mark = document.createElementNS(NS, 'svg');
    mark.setAttribute('width', '24');
    mark.setAttribute('height', '24');
    mark.setAttribute('viewBox', '0 0 24 24');
    mark.setAttribute('stroke', 'currentColor');
    mark.setAttribute('stroke-width', '1.5');
    mark.setAttribute('fill', 'none');
    mark.setAttribute('stroke-linejoin', 'round');
    mark.setAttribute('aria-hidden', 'true');
    mark.setAttribute('focusable', 'false');
    mark.setAttribute('role', 'presentation');
    mark.setAttribute('class', 'icon');
    var path = document.createElementNS(NS, 'path');
    path.setAttribute('d', 'M5 19 19 5M5 5l14 14');
    mark.appendChild(path);

    var text = document.createElement('span');
    text.textContent = 'Fits ' + summary + 'mm';
    chip.appendChild(mark);
    chip.appendChild(text);
    wrap.insertBefore(chip, wrap.firstChild);
  }

  /* ---------- Result count ----------
     The toolbar count lives outside #filter-results, so AJAX filtering never touches it.
     Read the filtered total published inside the swapped region and mirror it out. */
  function syncResultCount() {
    var src = document.querySelector('[data-wc-result-count]');
    var target = document.querySelector('.wc-resultcount__num');
    if (!src || !target) return;
    var n = parseInt(src.getAttribute('data-wc-result-count'), 10);
    if (isNaN(n)) return;
    // Search caps at 1,000 returned results while still counting facets over the true
    // match set, so the section flags a capped total and we render "1,000+". Collections
    // never set the attribute, so they are unaffected.
    var capped = src.getAttribute('data-wc-result-capped') === 'true';
    target.textContent = n.toLocaleString() + (capped ? '+' : '');
  }

  // Liquid has no thousands separator, so the pagination footer's total renders as
  // "3626" while the toolbar shows "3,626". Format it here for consistency.
  function formatShowingTotals() {
    Array.prototype.forEach.call(document.querySelectorAll('.wc-pagefoot__showing strong'), function (el) {
      var t = el.textContent.trim();
      if (/[–-]/.test(t)) return;                 // leave the "1–24" range alone
      var plus = t.indexOf('+') !== -1;           // "1000+" on capped search results
      var n = parseInt(t.replace(/[^0-9]/g, ''), 10);
      if (!isNaN(n)) el.textContent = n.toLocaleString() + (plus ? '+' : '');
    });
  }

  /* ---------- Collapsible description (mobile) ----------
     Only reveal the toggle when the description is genuinely clamped, so it is never
     a control that does nothing. */
  function syncDescToggle() {
    var desc = document.querySelector('[data-wc-desc]');
    var btn = document.querySelector('[data-wc-desc-toggle]');
    if (!desc || !btn) return;
    if (desc.classList.contains('is-expanded')) { btn.hidden = false; return; }
    btn.hidden = desc.scrollHeight <= desc.clientHeight + 1;
  }

  function toggleDesc(btn) {
    var desc = document.querySelector('[data-wc-desc]');
    if (!desc) return;
    var expanded = desc.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    btn.textContent = expanded ? 'Show less' : 'Read more';
  }

  function init() {
    if (!root()) return;
    buildFits();
    buildRailHead();
    stripCountParens();
    openDefaultGroups();
    decoratePriceFilter();
    syncResultCount();
    formatShowingTotals();
    syncDimensionChips();
    syncDescToggle();
    window.addEventListener('resize', syncDescToggle);
    rvRender();
    document.addEventListener('click', onClick);
    document.addEventListener('input', onInput);
    // facet-filters.js swaps #facets after AJAX — re-inject Fits-my-space.
    document.addEventListener('on:facet-filters:updated', function () {
      buildFits();
      syncAllFits();
      renderFitsWarning();   // applyFits early-returns when a rebuilt facet has no inputs
      if (lastFitsKey) {
        var back = document.querySelector('[data-wc-fits-input="' + lastFitsKey + '"]');
        if (back && document.activeElement !== back) {
          back.focus();
          var v = back.value; back.value = ''; back.value = v;   // caret to end
        }
      }
      buildRailHead();
      stripCountParens();
      openDefaultGroups();
      decoratePriceFilter();
      formatShowingTotals();
      restoreFitsFromUrl();
      syncResultCount();
      syncDimensionChips();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
